import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  IconButton,
  Tooltip,
  Divider,
  Box,
  CssBaseline,
  useMediaQuery,
  List,
  Typography,
  Button,
} from "@mui/material";
import PageLoader from "../theme/GenericLoader/PageLoader";
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Apps as AppsIcon,
} from "@mui/icons-material";
import { getMenuConfig } from "./MenuConfig";
import {
  DRAWER_WIDTH,
  COLLAPSED_DRAWER_WIDTH,
  MOBILE_BREAKPOINT,
  LayoutContainer,
  SidebarContainer,
  DesktopDrawer,
  MobileDrawer,
  DrawerHeader,
  LogoSection,
  LogoText,
  LogoIcon,
  ToggleButton,
  UserSection,
  UserAvatar,
  UserInfo,
  SectionTitle,
  DrawerFooter,
  MenuContent,
  StyledListItem,
  StyledListItemIcon,
  StyledListItemText,
  StyledBadge,
  MobileMenuButton,
  MobileCloseButton,
  MainContent,
  CustomBackdrop
} from "./DrawerStyles";
import { useTheme } from "@mui/material/styles";
import {
  Link,
  useLocation,
  useHistory,
} from "react-router-dom";
import { useSelector } from "react-redux";

// Import assets and utilities
import Store from "../index";
import helpers from "../UtilitiesFunctions";
import ThemeToggle from "../theme/ThemeToggle";

function ResponsiveDrawer(props) {
  const theme = useTheme();
  const location = useLocation();
  const history = useHistory();

  // RESPONSIVE DETECTION
  const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));
  const {
    token,
    userInfo,
    userRole,
    authenticated,
    cartlength,
    bussinessType,
    appType,
    onLogout,
    keycloak,
  } = props;

  // STATE MANAGEMENT
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);

  // Redux state
  const { companyName, instanceId, keyCloakAuth, gatewayUrl, basePath } = useSelector(
    (state) => state.globalState,
  );

  // EFFECT TO HANDLE SCREEN SIZE CHANGES
  useEffect(() => {
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [isMobile]);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // HANDLERS
  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleDesktopDrawerToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const logout = async () => {
    helpers.clearData();
    history.push("/");
  };

  // User role checks
  var isAdmin = userRole === "Admin" || userRole === "ADMIN";
  var isManager = userRole === "Manager" || userRole === "MANAGER";
  var isAgent = userRole === "Agent" || userRole === "AGENT";

  const getCurrentMenuItems = () => {
    const bussinessType = Store.getState()?.globalState?.bussinessType;
    return getMenuConfig(userRole, appType, bussinessType, cartlength);
  };

  const renderMenuItem = (item, index, drawerOpen) => {
    const isItemActive = isActive(item.path);
    // Handle action items (like logout)
    if (item.action) {
      const actionItem = (
        <StyledListItem
          onClick={() => {
            if (item.action === "logout") {
              logout();
            }
            if (isMobile) {
              handleMobileDrawerClose();
            }
          }}
          open={drawerOpen}
          key={index}
        >
          <StyledListItemIcon open={drawerOpen}>
            {item.badge ? (
              <StyledBadge badgeContent={item.badge}>{item.icon}</StyledBadge>
            ) : (
              item.icon
            )}
          </StyledListItemIcon>
          <StyledListItemText primary={item.name} open={drawerOpen} />
        </StyledListItem>
      );
      if (!drawerOpen) {
        return (
          <Tooltip
            key={index}
            title={item.name}
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: theme.palette.text.primary,
                  color: theme.palette.common.white,
                  fontSize: "0.75rem",
                  fontWeight: 500,
                },
              },
              arrow: {
                sx: {
                  color: theme.palette.text.primary,
                },
              },
            }}
          >
            {actionItem}
          </Tooltip>
        );
      }
      return actionItem;
    }
    // Handle navigation items (with path)
    const listItem = (
      <StyledListItem
        component={Link}
        to={item.path}
        active={isItemActive}
        open={drawerOpen}
        key={index}
        onClick={() => {
          if (isMobile) {
            handleMobileDrawerClose();
          }
        }}
      >
        <StyledListItemIcon open={drawerOpen}>
          {item.badge ? (
            <StyledBadge badgeContent={item.badge}>{item.icon}</StyledBadge>
          ) : (
            item.icon
          )}
        </StyledListItemIcon>
        <StyledListItemText primary={item.name} open={drawerOpen} />
      </StyledListItem>
    );
    if (!drawerOpen) {
      return (
        <Tooltip
          key={index}
          title={item.name}
          placement="right"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: theme.palette.text.primary,
                color: theme.palette.common.white,
                fontSize: "0.75rem",
                fontWeight: 500,
              },
            },
            arrow: {
              sx: {
                color: theme.palette.text.primary,
              },
            },
          }}
        >
          {listItem}
        </Tooltip>
      );
    }
    return listItem;
  };

  // DRAWER CONTENT
  const drawer = (drawerOpen, isMobileDrawer = false) => (
    <>
      {/* Header */}
      <DrawerHeader open={drawerOpen}>
        <LogoSection open={drawerOpen}>
          {(() => {
            const cleanType = appType ? appType.replace('-', '') : '';

            // Only keep icons for non-WEBSITE apps
            const iconMap = {
              'HPOS': `${process.env.PUBLIC_URL}/google-analytics.png`,
              'HPiMS': `${process.env.PUBLIC_URL}/inventory.png`,
              'HCRM': `${process.env.PUBLIC_URL}/crm.png`,
              'HOMS': `${process.env.PUBLIC_URL}/checklist.png`,
              'HPRMS': `${process.env.PUBLIC_URL}/procurement.png`,
              'HRMS': `${process.env.PUBLIC_URL}/report.png`,
              'HVMS': `${process.env.PUBLIC_URL}/vendor.png`,
              'ZATCA': `${process.env.PUBLIC_URL}/ZATCA.png`,
              'ACCOUNTS': `${process.env.PUBLIC_URL}/ACCOUNTS.png`,
            };
            const iconSrc = iconMap[cleanType];

            return (
              <>
                {iconSrc ? (
                  <img
                    src={iconSrc}
                    alt={cleanType}
                    style={{
                      height: drawerOpen ? '30px' : '30px',
                      width: 'auto',
                      transition: 'height 0.2s ease'
                    }}
                  />
                ) : (
                  <LogoIcon />
                )}
                <LogoText variant="h6" open={drawerOpen}>
                  {cleanType || 'HULM SUITS'}
                </LogoText>
              </>
            );
          })()}
        </LogoSection>
        {isMobileDrawer && (
          <MobileCloseButton onClick={handleMobileDrawerClose}>
            <CloseIcon />
          </MobileCloseButton>
        )}
      </DrawerHeader>

      {/* Scrollable Menu Content */}
      <MenuContent>
        <Box sx={{ py: 4 }}>
          {/* Menu Sections - Filter out ACCOUNT section */}
          {getCurrentMenuItems().filter(section => section.section !== "ACCOUNT").map((section, sectionIndex) => (
            <Box key={sectionIndex}>
              <SectionTitle open={drawerOpen}>{section.section}</SectionTitle>
              <List disablePadding sx={{ mb: 1 }}>
                {section.items.map((item, itemIndex) =>
                  renderMenuItem(
                    item,
                    `${sectionIndex}-${itemIndex}`,
                    drawerOpen,
                  ),
                )}
              </List>

              {sectionIndex < getCurrentMenuItems().filter(s => s.section !== "ACCOUNT").length - 1 &&
                drawerOpen && (
                  <Box sx={{ mx: 2, my: 0.5 }}>
                    <Divider sx={{ backgroundColor: theme.palette.divider }} />
                  </Box>
                )}
            </Box>
          ))}
        </Box>
      </MenuContent>

      <DrawerFooter>
        {/* Go to Apps Button */}
        <Box sx={{ p: drawerOpen ? 2 : 1, pb: 0, display: 'flex', justifyContent: 'center' }}>
          {drawerOpen ? (
            <Button
              fullWidth
              variant="outlined"
              href="https://app.hulmsolutions.com/dashboard"
              target="_blank"
              startIcon={<AppsIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: theme.palette.primary.dark,
                borderColor: theme.palette.primary.dark,
                '&:hover': {
                  borderColor: theme.palette.primary.dark,
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              Go to Apps
            </Button>
          ) : (
            <Tooltip title="Go to Apps" placement="right">
              <IconButton
                href="https://app.hulmsolutions.com/dashboard"
                target="_blank"
                sx={{
                  color: theme.palette.primary.dark,
                  border: `1px solid ${theme.palette.primary.dark}`,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  }
                }}
              >
                <AppsIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {userInfo && (
          <UserSection open={drawerOpen}>
            <UserAvatar>
              {userInfo.name ? (
                userInfo.name.charAt(0).toUpperCase()
              ) : (
                <PersonIcon />
              )}
            </UserAvatar>
            <UserInfo open={drawerOpen}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontSize: "0.875rem",
                  lineHeight: 1.2,
                  mb: 0.25,
                }}
              >
                {userInfo.name || userInfo.given_name || "Super Admin"}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "0.75rem",
                  fontWeight: 500,
                }}
              >
                {userRole || "Admin"}
              </Typography>
            </UserInfo>
            {/* Theme Toggle next to user */}
            <Box sx={{ ml: 'auto' }}>
              <ThemeToggle sx={{ width: 32, height: 32 }} />
            </Box>
          </UserSection>
        )}

        {/* Account Section (Logout) */}
        <List disablePadding sx={{ mb: 0 }}>
          {getCurrentMenuItems()
            .find(section => section.section === "ACCOUNT")
            ?.items.map((item, index) =>
              renderMenuItem(item, `footer-${index}`, drawerOpen)
            )}
        </List>
      </DrawerFooter>
    </>
  );

  // PREVENT BODY SCROLL WHEN MOBILE DRAWER IS OPEN
  useEffect(() => {
    if (mobileOpen && isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileOpen, isMobile]);

  return (
    <LayoutContainer>
      <CssBaseline />

      {/* Mobile Menu Button */}
      {isMobile && (
        <MobileMenuButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleMobileDrawerToggle}
        >
          <MenuIcon />
        </MobileMenuButton>
      )}

      {/* Sidebar - Only render when authenticated and authorized for non-WEBSITE apps */}
      {authenticated && (isAdmin || isManager || isAgent) && appType !== "WEBSITE-" && (
        <>
          {/* Desktop Sidebar */}
          <SidebarContainer>
            <DesktopDrawer open={desktopOpen}>
              {drawer(desktopOpen, false)}
              <ToggleButton onClick={handleDesktopDrawerToggle} size="small" open={desktopOpen}>
                {desktopOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
              </ToggleButton>
            </DesktopDrawer>
          </SidebarContainer>

          {/* Mobile Drawer */}
          {isMobile && (
            <>
              <CustomBackdrop
                open={mobileOpen}
                onClick={handleMobileDrawerClose}
              />
              <MobileDrawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleMobileDrawerClose}
                ModalProps={{
                  keepMounted: true,
                  disableScrollLock: false,
                }}
              >
                {drawer(true, true)}
              </MobileDrawer>
            </>
          )}
        </>
      )}

      {/* Main Content */}
      <MainContent isMobile={isMobile}>
        <Suspense fallback={<PageLoader />}>
          <Box
            sx={{
              padding: 0,
              minHeight: "100vh",
              [theme.breakpoints.down("sm")]: {
                padding: 0,
                paddingTop: theme.spacing(8),
              },
            }}
          >
            {/* App Routes */}
            <Box>{props.children}</Box>
          </Box>
        </Suspense>
      </MainContent>
    </LayoutContainer>
  );
}

ResponsiveDrawer.defaultProps = {
  authenticated: false,
  userInfo: {
    preferred_username: "guest",
  },
};

export default ResponsiveDrawer;